import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { errorMessages } from '~/constants/errorMessages.ts';
import api from '~/libs/api';
import { TTobo } from './types';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { getRegionsOptions } from './utils';

const getTobos = () => async () => {
  try {
    const { data } = await api.get(`/dictionary-service/tobo/filtered`);
    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

export const useServicePointsQuery = (regionId?: string | null) => {
  const { data, error, isLoading, isError } = useQuery<TTobo[]>({
    queryKey: ['tobos'],
    queryFn: getTobos(),
    staleTime: 30 * 60 * 1000,
  });

  const regions: TAutocompleteOption[] = useMemo(() => {
    if (!data) return [];
    return getRegionsOptions(data);
  }, [data]);

  const allServicePoints: TAutocompleteOption[] = useMemo(() => {
    if (!data) return [];
    return data
      .map((t) => ({
        value: t.toboid.toString(),
        label: t.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const servicePoints: TAutocompleteOption[] = useMemo(() => {
    if (!data || !regionId) return [];
    return data
      .filter((t) => t.regionid.toString() === regionId)
      .map((t) => ({
        value: t.toboid.toString(),
        label: t.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data, regionId]);

  return { regions, servicePoints, allServicePoints, error, isLoading, isError };
};
